import { useState, useEffect, useRef, memo } from 'react';
import cn from 'classnames';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';

import NavItem, { Content } from '@/components/nav/item';
import {
  Solutions,
  Product,
  Resources,
  Developers,
} from '@/components/nav/content';
import { useNavigationState } from '@/contexts/navigation';

import styles from './main-nav.module.css';

type Dropdown = 'solutions' | 'product' | 'resources' | 'developers' | '';

type DropdownItem = {
  id: Dropdown;
  title: string;
  content: Content;
};

const dropdownItems: DropdownItem[] = [
  {
    id: `product`,
    title: `Product`,
    content: Product,
  },
  {
    id: `solutions`,
    title: `Solutions`,
    content: Solutions,
  },
  {
    id: `resources`,
    title: `Resources`,
    content: Resources,
  },
  {
    id: `developers`,
    title: `Developers`,
    content: Developers,
  },
];

function MainNav({ isDesktop }: { isDesktop: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isOpen } = useNavigationState();
  const [activeDropdown, setActiveDropdown] = useState<Dropdown>(``);

  function onMouseEnter(dropdown: Dropdown) {
    setActiveDropdown(dropdown);
  }

  function onMouseLeave() {
    setActiveDropdown(``);
  }

  useEffect(
    () => () => {
      clearAllBodyScrollLocks();
    },
    [],
  );

  useEffect(() => {
    if (!isDesktop) onMouseLeave();
  }, [isDesktop]);

  useEffect(() => {
    if (scrollRef.current) {
      if (isOpen) {
        disableBodyScroll(scrollRef.current);
      } else {
        enableBodyScroll(scrollRef.current);
      }
    }
  }, [isOpen]);

  return (
    <nav
      ref={scrollRef}
      className={cn(
        `w-auto px-4 lg:px-0 flex-col lg:flex-row lg:items-center lg:justify-center`,
        {
          'hidden lg:flex': !isOpen,
          'border-gray-300 p-4 mt-6 border-transparent lg:border-none overflow-y-auto':
            isOpen,
        },
      )}
      style={{
        WebkitOverflowScrolling: `touch`,
      }}
    >
      <div
        onMouseLeave={onMouseLeave}
        className={cn(`justify-center lg:flex lg:flex-row`, styles.nav)}
      >
        {dropdownItems.map((item) => (
          <NavItem
            key={item.id}
            label={item.title}
            content={item.content}
            onMouseEnter={() => onMouseEnter(item.id)}
            dropdownOpen={activeDropdown === item.id}
            isDesktop={isDesktop}
          />
        ))}
      </div>

      <NavItem label="Pricing" href="/pricing" className={styles.pricing} />
    </nav>
  );
}

export default memo(MainNav);
